/**
 * Published Questions Report Component
 * Displays a report of published questions with filters and export options
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { useReportsStore } from "../../stores/reports.store";
import { useFiltersStore } from "../../stores/filters.store";
import { useToast } from "../../contexts/ToastContext";
import { useAuthStore } from "../../stores/auth.store";
import { exportQuestionsReport } from "../../services/reports.service";
import FilterDropdowns from "../filterDropdowns/FilterDropdowns";
import SearchInput from "../searchInput/SearchInput";
import Calendar from "../calendar/Calendar";
import Pagination from "../pagination/Pagination";
import "./PublishedQuestionsReport.scss";

const PublishedQuestionsReport: React.FC = () => {
  const navigate = useNavigate();
  const { getResourceValue } = useResources();
  const { showError, showSuccess } = useToast();
  const { user } = useAuthStore();
  
  // Stores
  const { questions, pagination, isLoading, fetchQuestionsReport, reset: resetReports } = useReportsStore();
  const {
    selectedCountryIsoCode,
    selectedGradeId,
    selectedLanguageIsoCode,
    selectedSubjectId,
    selectedDisciplineId,
    resetFilters,
  } = useFiltersStore();
  
  // Helper function to format date for display (DD/MM/YYYY)
  const formatDate = (date: Date) => {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Helper function to format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Helper function to parse DD/MM/YYYY to Date
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  // Get yesterday's date
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  // Get today's date
  const getToday = () => {
    return new Date();
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState(formatDate(getYesterday()));
  const [toDate, setToDate] = useState(formatDate(getToday()));
  const [updatedFromDate, setUpdatedFromDate] = useState<string>("");
  const [updatedToDate, setUpdatedToDate] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [showUpdatedFromCalendar, setShowUpdatedFromCalendar] = useState(false);
  const [showUpdatedToCalendar, setShowUpdatedToCalendar] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isCSVQueued, setIsCSVQueued] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  
  const fromCalendarRef = useRef<HTMLDivElement>(null);
  const toCalendarRef = useRef<HTMLDivElement>(null);
  const updatedFromCalendarRef = useRef<HTMLDivElement>(null);
  const updatedToCalendarRef = useRef<HTMLDivElement>(null);

  // Close calendars when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromCalendarRef.current && !fromCalendarRef.current.contains(event.target as Node)) {
        setShowFromCalendar(false);
      }
      if (toCalendarRef.current && !toCalendarRef.current.contains(event.target as Node)) {
        setShowToCalendar(false);
      }
      if (updatedFromCalendarRef.current && !updatedFromCalendarRef.current.contains(event.target as Node)) {
        setShowUpdatedFromCalendar(false);
      }
      if (updatedToCalendarRef.current && !updatedToCalendarRef.current.contains(event.target as Node)) {
        setShowUpdatedToCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset filters and report data when component unmounts
  useEffect(() => {
    return () => {
      resetFilters();
      resetReports();
    };
  }, [resetFilters, resetReports]);

  // Re-enable CSV button when dropdown filters change
  useEffect(() => {
    setIsCSVQueued(false);
  }, [selectedCountryIsoCode, selectedGradeId, selectedLanguageIsoCode, selectedSubjectId, selectedDisciplineId]);

  const totalRecords = pagination?.total_records || 0;
  const totalPages = pagination?.total_pages || 0;

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    // Re-enable CSV button when search changes
    setIsCSVQueued(false);
  }, []);

  const handleSearchKeyDown = () => {
    // Call apply filters when Enter is pressed and there's a search value
    if (searchQuery?.trim()) {
      handleApplyFilters();
    }
  };

  // Helper function to check if search query is a 12-digit Question ID
  const isQuestionIdSearch = (query: string): boolean => {
    const trimmedQuery = query?.trim() || '';
    return /^\d{12}$/.test(trimmedQuery);
  };

  const handleCSVExport = async () => {
    if (isCSVQueued) return; // Prevent multiple clicks while queued

    setIsExporting(true);
    try {
      // Prepare export parameters (without pagination)
      const fromDateObj = parseDate(fromDate);
      const toDateObj = parseDate(toDate);
      
      const updatedFromDateObj = parseDate(updatedFromDate);
      const updatedToDateObj = parseDate(updatedToDate);

      const params = {
        country_iso_code: selectedCountryIsoCode || undefined,
        grade_id: selectedGradeId || undefined,
        language_iso_code: selectedLanguageIsoCode || undefined,
        subject_id: selectedSubjectId || undefined,
        discipline_id: selectedDisciplineId || undefined,
        search_text: searchQuery?.trim() || undefined,
        created_from: fromDateObj ? formatDateForAPI(fromDateObj) : undefined,
        created_to: toDateObj ? formatDateForAPI(toDateObj) : undefined,
        updated_from: updatedFromDateObj ? formatDateForAPI(updatedFromDateObj) : undefined,
        updated_to: updatedToDateObj ? formatDateForAPI(updatedToDateObj) : undefined,
        user_email: user?.email || undefined,
      };

      console.log('Exporting CSV with params:', params);

      // Call export API to queue the CSV generation
      await exportQuestionsReport(params);
      
      // Show success toast
      showSuccess(
        getResourceValue("csv_export_queued", "CSV Export Queued"),
        getResourceValue("csv_export_queued_message", "Your CSV export request has been added to the queue. You will receive an email when it's ready.")
      );
      
      // Disable the button
      setIsCSVQueued(true);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("csv_export_failed", "Failed to queue CSV export. Please try again.")
      );
    } finally {
      setIsExporting(false);
    }
  };


  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    
    const fromDateObj = parseDate(fromDate);
    const toDateObj = parseDate(toDate);
    const updatedFromDateObj = parseDate(updatedFromDate);
    const updatedToDateObj = parseDate(updatedToDate);
    
    const params = {
      country_iso_code: selectedCountryIsoCode || undefined,
      grade_id: selectedGradeId || undefined,
      language_iso_code: selectedLanguageIsoCode || undefined,
      subject_id: selectedSubjectId || undefined,
      discipline_id: selectedDisciplineId || undefined,
      search_text: searchQuery?.trim() || undefined,
      page_number: page,
      page_size: pageSize,
      created_from: fromDateObj ? formatDateForAPI(fromDateObj) : undefined,
      created_to: toDateObj ? formatDateForAPI(toDateObj) : undefined,
      updated_from: updatedFromDateObj ? formatDateForAPI(updatedFromDateObj) : undefined,
      updated_to: updatedToDateObj ? formatDateForAPI(updatedToDateObj) : undefined,
    };

    await fetchQuestionsReport(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = async (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    
    const fromDateObj = parseDate(fromDate);
    const toDateObj = parseDate(toDate);
    const updatedFromDateObj = parseDate(updatedFromDate);
    const updatedToDateObj = parseDate(updatedToDate);
    
    const params = {
      country_iso_code: selectedCountryIsoCode || undefined,
      grade_id: selectedGradeId || undefined,
      language_iso_code: selectedLanguageIsoCode || undefined,
      subject_id: selectedSubjectId || undefined,
      discipline_id: selectedDisciplineId || undefined,
      search_text: searchQuery?.trim() || undefined,
      page_number: 1,
      page_size: size,
      created_from: fromDateObj ? formatDateForAPI(fromDateObj) : undefined,
      created_to: toDateObj ? formatDateForAPI(toDateObj) : undefined,
      updated_from: updatedFromDateObj ? formatDateForAPI(updatedFromDateObj) : undefined,
      updated_to: updatedToDateObj ? formatDateForAPI(updatedToDateObj) : undefined,
    };

    await fetchQuestionsReport(params);
  };

  const handleFromDateSelect = (date: Date) => {
    const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    setFromDate(formattedDate);
    setShowFromCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleToDateSelect = (date: Date) => {
    const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    setToDate(formattedDate);
    setShowToCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleFromDateClear = () => {
    setFromDate('');
    setShowFromCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleToDateClear = () => {
    setToDate('');
    setShowToCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleUpdatedFromDateSelect = (date: Date) => {
    const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    setUpdatedFromDate(formattedDate);
    setShowUpdatedFromCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleUpdatedToDateSelect = (date: Date) => {
    const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    setUpdatedToDate(formattedDate);
    setShowUpdatedToCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleUpdatedFromDateClear = () => {
    setUpdatedFromDate('');
    setShowUpdatedFromCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  const handleUpdatedToDateClear = () => {
    setUpdatedToDate('');
    setShowUpdatedToCalendar(false);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
  };

  // Validation handlers for manual date input
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFromDate(value);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
    
    // Validate if the date is complete (DD/MM/YYYY format)
    const dateObj = parseDate(value);
    if (dateObj && dateObj > getToday()) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("date_cannot_be_future", "Date cannot be greater than today")
      );
      setFromDate(formatDate(getToday()));
    }
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToDate(value);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
    
    // Validate if the date is complete (DD/MM/YYYY format)
    const dateObj = parseDate(value);
    if (dateObj && dateObj > getToday()) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("date_cannot_be_future", "Date cannot be greater than today")
      );
      setToDate(formatDate(getToday()));
    }
  };

  const handleUpdatedFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUpdatedFromDate(value);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
    
    // Validate if the date is complete (DD/MM/YYYY format)
    const dateObj = parseDate(value);
    if (dateObj && dateObj > getToday()) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("date_cannot_be_future", "Date cannot be greater than today")
      );
      setUpdatedFromDate(formatDate(getToday()));
    }
  };

  const handleUpdatedToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUpdatedToDate(value);
    // Re-enable CSV button when date filter changes
    setIsCSVQueued(false);
    
    // Validate if the date is complete (DD/MM/YYYY format)
    const dateObj = parseDate(value);
    if (dateObj && dateObj > getToday()) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("date_cannot_be_future", "Date cannot be greater than today")
      );
      setUpdatedToDate(formatDate(getToday()));
    }
  };

  const handleApplyFilters = async () => {
    // Convert dates from DD/MM/YYYY to YYYY-MM-DD for API
    const fromDateObj = parseDate(fromDate);
    const toDateObj = parseDate(toDate);
    const updatedFromDateObj = parseDate(updatedFromDate);
    const updatedToDateObj = parseDate(updatedToDate);
    
    // Validate created date range
    if (fromDateObj && toDateObj && fromDateObj > toDateObj) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("created_from_greater_than_to", "Created date from cannot be greater than created date to")
      );
      return;
    }

    // Validate updated date range
    if (updatedFromDateObj && updatedToDateObj && updatedFromDateObj > updatedToDateObj) {
      showError(
        getResourceValue("validation_error", "Validation Error"),
        getResourceValue("updated_from_greater_than_to", "Updated date from cannot be greater than updated date to")
      );
      return;
    }
    
    // Mark that filters have been applied
    setHasAppliedFilters(true);
    
    // Re-enable CSV button when filters change
    setIsCSVQueued(false);
    
    const params = {
      country_iso_code: selectedCountryIsoCode || undefined,
      grade_id: selectedGradeId || undefined,
      language_iso_code: selectedLanguageIsoCode || undefined,
      subject_id: selectedSubjectId || undefined,
      discipline_id: selectedDisciplineId || undefined,
      search_text: searchQuery?.trim() || undefined,
      page_number: 1,
      page_size: pageSize,
      created_from: fromDateObj ? formatDateForAPI(fromDateObj) : undefined,
      created_to: toDateObj ? formatDateForAPI(toDateObj) : undefined,
      updated_from: updatedFromDateObj ? formatDateForAPI(updatedFromDateObj) : undefined,
      updated_to: updatedToDateObj ? formatDateForAPI(updatedToDateObj) : undefined,
    };

    console.log('Applying filters with params:', params);
    setCurrentPage(1);
    await fetchQuestionsReport(params);
  };

  const handlePreview = () => {
    // Extract all Question IDs from the current questions array
    // Note: We only get IDs from the current page, but we could fetch all if needed
    const questionIds = questions
      .map((row) => row["Question ID"])
      .filter((id): id is string => Boolean(id));
    
    if (questionIds.length === 0) {
      return;
    }
    
    // Navigate to existing preview results page with all question IDs
    navigate("/preview-questions/results", {
      state: {
        questionIds: questionIds,
        source: "publishedQuestionsReport"
      }
    });
  };

  const handleQuestionClick = (questionId: string) => {
    if (!questionId) return;
    
    // Navigate to preview page for single question
    navigate(`/preview-questions/results/${questionId}`);
  };

  return (
    <div className="page published-report-page">
      {/* Filters Section - Row 1: dropdowns + Created from/to; Row 2: Updated from/to + Apply */}
      <div className="published-report-filters">
        <div className="published-report-filters-row published-report-filters-row--first">
          <FilterDropdowns
            className="published-report-filter-dropdowns"
            showCountry={true}
            showGrade={true}
            showSubject={true}
            showLanguage={true}
            showDiscipline={true}
          />
          <div className="date-picker" ref={fromCalendarRef}>
            <input
              type="text"
              value={fromDate}
              onChange={handleFromDateChange}
              placeholder={formatDate(getYesterday())}
              className="date-picker__input"
              onClick={() => setShowFromCalendar(!showFromCalendar)}
            />
            <i className="f-icon-right calendar-icon" onClick={() => setShowFromCalendar(!showFromCalendar)}></i>
            {showFromCalendar && (
              <Calendar
                initialDate={parseDate(fromDate)}
                onDateSelect={handleFromDateSelect}
                onClear={handleFromDateClear}
                maxDate={new Date()}
              />
            )}
          </div>
          <div className="date-picker" ref={toCalendarRef}>
            <input
              type="text"
              value={toDate}
              onChange={handleToDateChange}
              placeholder={formatDate(getToday())}
              className="date-picker__input"
              onClick={() => setShowToCalendar(!showToCalendar)}
            />
            <i className="f-icon-right calendar-icon" onClick={() => setShowToCalendar(!showToCalendar)}></i>
            {showToCalendar && (
              <Calendar
                initialDate={parseDate(toDate)}
                onDateSelect={handleToDateSelect}
                onClear={handleToDateClear}
                maxDate={new Date()}
              />
            )}
          </div>
        </div>
        <div className="published-report-filters-row published-report-filters-row--second">
          <div className="date-picker" ref={updatedFromCalendarRef}>
            <input
              type="text"
              value={updatedFromDate}
              onChange={handleUpdatedFromDateChange}
              placeholder="Updated date from"
              className="date-picker__input"
              onClick={() => setShowUpdatedFromCalendar(!showUpdatedFromCalendar)}
            />
            <i className="f-icon-right calendar-icon" onClick={() => setShowUpdatedFromCalendar(!showUpdatedFromCalendar)}></i>
            {showUpdatedFromCalendar && (
              <Calendar
                initialDate={parseDate(updatedFromDate)}
                onDateSelect={handleUpdatedFromDateSelect}
                onClear={handleUpdatedFromDateClear}
                maxDate={new Date()}
              />
            )}
          </div>
          <div className="date-picker" ref={updatedToCalendarRef}>
            <input
              type="text"
              value={updatedToDate}
              onChange={handleUpdatedToDateChange}
              placeholder="Updated date to"
              className="date-picker__input"
              onClick={() => setShowUpdatedToCalendar(!showUpdatedToCalendar)}
            />
            <i className="f-icon-right calendar-icon" onClick={() => setShowUpdatedToCalendar(!showUpdatedToCalendar)}></i>
            {showUpdatedToCalendar && (
              <Calendar
                initialDate={parseDate(updatedToDate)}
                onDateSelect={handleUpdatedToDateSelect}
                onClear={handleUpdatedToDateClear}
                maxDate={new Date()}
              />
            )}
          </div>
          <button
            className="btn btn-green apply-filters-btn"
            onClick={handleApplyFilters}
          >
            {getResourceValue("apply_filters", "Apply Filters")}
          </button>
        </div>
      </div>

      {/* Header Section */}
      <div className="published-report-header">
        <h1 className="published-report-title">
          {getResourceValue("published_questions_report", "Published Questions Report")}
        </h1>
        
        <div className="published-report-actions">
          <button
            className="btn btn-outline"
            onClick={handlePreview}
            disabled={questions.length === 0 || isLoading}
          >
            <i className="f-icon eye-icon"></i>
            {getResourceValue("preview_questions", "Preview")}
          </button>
          <button
            className="btn btn-green csv-download-btn"
            onClick={handleCSVExport}
            disabled={!hasAppliedFilters || isLoading || isExporting || isCSVQueued}
            style={{ opacity: (!hasAppliedFilters || isCSVQueued) ? 0.5 : 1, cursor: (!hasAppliedFilters || isCSVQueued) ? 'not-allowed' : 'pointer' }}
          >
            <i className="f-icon download-icon"></i>
            {isExporting ? getResourceValue("exporting", "Exporting...") : "CSV"}
          </button>
        </div>
      </div>

      {/* Table Section */}
      <section className="table-container">
        <div className="table-wrapper">
          <div className="table-filter-container">
            <div className="search-section">
              <SearchInput
                value={searchQuery}
                onChange={handleSearch}
                placeholder={getResourceValue("search", "Search")}
                onKeyDown={handleSearchKeyDown}
              />
            </div>

            <div className="sorting-section">
              <div className="sorting-controls">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              </div>
            </div>
          </div>

          <div className="table-scroll-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{getResourceValue("question_id", "Question ID")}</th>
                  <th>{getResourceValue("parent_id", "Parent ID")}</th>
                  <th>{getResourceValue("section_id", "Section ID")}</th>
                  <th>{getResourceValue("country", "Country")}</th>
                  <th>{getResourceValue("subject", "Subject")}</th>
                  <th>{getResourceValue("grade", "Grade")}</th>
                  <th>{getResourceValue("language", "Language")}</th>
                  <th>{getResourceValue("question_type", "Question Type")}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="no-data">
                        {getResourceValue("loading", "Loading...")}
                      </div>
                    </td>
                  </tr>
                ) : questions.length > 0 ? (
                  questions.map((row, index) => {
                    const questionId = row["Question ID"];
                    return (
                      <tr 
                        key={index}
                        onClick={() => handleQuestionClick(questionId)}
                        className="published-report__table-row"
                      >
                        <td><span className="text-strong">{questionId}</span></td>
                        <td><span className="text-strong">{row["Parent ID"] || "N/A"}</span></td>
                        <td><span className="text-strong">{row["Section ID"]}</span></td>
                        <td><span className="text-strong">{row["Country"]}</span></td>
                        <td><span className="text-strong">{row["Subject"]}</span></td>
                        <td><span className="text-strong">{row["Grade"]}</span></td>
                        <td><span className="text-strong">{row["Language"]}</span></td>
                        <td><span className="text-strong">{row["Question Type"]}</span></td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <div className="no-data">
                        {isQuestionIdSearch(searchQuery)
                          ? getResourceValue("question_id_not_found", "Question ID not found.")
                          : getResourceValue("no_data_found", "No data found matching your criteria.")}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublishedQuestionsReport;
